/*
 * Active's display state, derived from the payload (direction §9).
 *
 * Separated from the component on purpose: every rule below is a spec sentence,
 * and each is testable as a pure function over a payload shape. The component's
 * job is then only to render what these return, which keeps §9's state stack
 * legible in one file instead of spread across JSX branches.
 */

import { list, type ActiveEvent, type ActiveFencer, type ActiveTournament } from "./api";
import { deTableLabel } from "./deTable";
import { isDone, isVisible, type GracePolicy } from "./grace";
import { stalenessTier, type StalenessTier } from "./staleness";
import { shifted, venueClock } from "./zones";

/**
 * Whether FTL has a page for this event.
 *
 * PRESENCE IMPLIES STATE (contract §0): the server sends no coverage flag, and
 * the absence of a URL IS the no-coverage state. That is not a shortcut — it is
 * what makes the capability rule work, because the View pill renders strictly
 * from the presence of its destination and cannot get out of step with a boolean.
 */
export type Coverage = "covered" | "uncovered";

export function coverageOf(ev: ActiveEvent): Coverage {
  return ev.event_url ? "covered" : "uncovered";
}

/**
 * The round's state, as an i18n key plus params.
 *
 * WHY A KEY AND NOT A STRING. Two reasons, and the second is the load-bearing
 * one. §7 bars literals in components; and §9 wants HONEST VOCABULARY, which
 * means our own words rather than FTL's — FTL says "Pools in progress" and
 * "Table of 32", vocabularies that vary by scoring software version and do not
 * localize. The raw string is kept for the disclosure to quote (that is why the
 * contract returns both), but it is never the label.
 *
 * The three silences §6 keeps distinct, all reachable here:
 *   - uncovered: FTL has no page, so there is no round state to have.
 *   - not started: FTL has the page, no round is open yet.
 *   - went quiet: a round was open and we have stopped hearing about it.
 */
export interface RoundState {
  key: string;
  /** FTL's own words, for the disclosure. Never rendered as the label. */
  raw?: string | undefined;
  /** Pools finished of total, when FTL published both. */
  pools?: { complete: number; total: number } | undefined;
}

export function roundStateOf(
  ev: ActiveEvent,
  now: Date = new Date(),
  timeZone?: string | null,
): RoundState {
  const raw = ev.round_status_raw;

  if (coverageOf(ev) === "uncovered") {
    return { key: "active.round.uncovered" };
  }

  // A TWO-DAY EVENT WAITING FOR ITS NEXT DAY (decision 784) has its next round
  // drawn and nothing fenced in it. FTL's status still names the day before --
  // "Fencing elimination round" on Casablanca's Thursday row, all of Friday --
  // so the round reads as what it is: posted. The event line carries the start.
  if (resumesLater(ev, now, timeZone)) {
    const kind = ev.round_kind ?? "other";
    return {
      key: kind === "pool" ? "active.round.poolsAssigned" : "active.round.bracketPosted",
      raw,
    };
  }

  // THE EVENT'S OWN WORD COMES FIRST (decision 432). FTL states "Finished at
  // 1:49 PM" on the event itself, and no reading of the rounds can reproduce
  // it: an event first seen finished has no round and read "Not started"; an
  // event whose DE was marked complete read "Between rounds". Both were true
  // of the rounds and wrong about the event, which is what the 29-30 Aug and
  // 5-6 Sep weekends both showed. Only "complete" overrides here -- a live
  // event's round state is the finer-grained fact and stays in charge.
  if (ev.event_status === "complete") {
    return { key: "active.round.finished", raw: ev.event_status_raw ?? raw };
  }

  // No round fields at all. The server omits the whole group of them together, so
  // testing the status is enough to mean "no round is open".
  if (!ev.round_status) {
    return { key: "active.round.notStarted", raw };
  }

  const pools =
    ev.round_pools_complete != null && ev.round_pools_total != null
      ? { complete: ev.round_pools_complete, total: ev.round_pools_total }
      : undefined;

  const kind = ev.round_kind ?? "other";

  switch (ev.round_status) {
    case "not_started":
      return { key: "active.round.notStarted", raw, pools };

    case "preparing":
      // "Preparing" is FTL's word for a round that exists but has not begun.
      // Rendered per kind because "Pools assigned" and "Bracket posted" are what
      // a coach can act on; a generic "preparing" tells them nothing.
      return {
        key: kind === "pool" ? "active.round.poolsAssigned" : "active.round.bracketPosted",
        raw,
        pools,
      };

    case "fencing":
      return {
        key: kind === "pool" ? "active.round.fencingPools" : "active.round.fencingDEs",
        raw,
        pools,
      };

    case "complete":
      // §9's stack has "between rounds" as a state of its own. A completed round
      // with no later one open is exactly that: something has finished and
      // something else has not started, which is different from either.
      return { key: "active.round.betweenRounds", raw, pools };

    case "unknown":
    default:
      // FTL said something we do not have a word for. Say that, rather than
      // guessing a state — §4's voice rule is that the app never claims more than
      // it knows, and the raw string is available in the disclosure.
      return { key: "active.round.unknown", raw, pools };
  }
}

/**
 * Whether a multi-day event is between its days: a round of a later day is
 * posted (the server then dates the event by that day and sends the first day
 * as `event_continues_from`, decision 784) and that day has not begun. Begun is
 * its start when the schedule gave one, else the venue's calendar reaching it.
 */
export function resumesLater(ev: ActiveEvent, now: Date = new Date(), timeZone?: string | null): boolean {
  if (!ev.event_continues_from || !ev.event_date) return false;
  if (ev.event_status === "complete") return false;
  if (ev.event_starts_at) return now.getTime() < Date.parse(ev.event_starts_at);
  return venueDateOf(now, timeZone) < ev.event_date;
}

/**
 * Whether nothing about this event is owed yet: before its start, or its next
 * day's (decision 784). Neither the event's went-quiet state nor a fencer's
 * "strip seen" age is said then -- a table of 64 drawn on Thursday evening for
 * Saturday morning has strips that are a day and a half from mattering, and
 * "strip seen 2 hours ago" over them describes nothing.
 */
export function beforeStart(ev: ActiveEvent, now: Date = new Date()): boolean {
  return ev.event_starts_at != null && now.getTime() < Date.parse(ev.event_starts_at);
}

/**
 * Whether to withhold the round-state label entirely.
 *
 * "Between rounds" is the one label in the stack that CLAIMS SOMETHING IS
 * COMING. Every other one describes what is: pools are fencing, the bracket is
 * posted, the event is finished. So it is the one that can be wrong about the
 * future rather than merely out of date -- and it is reached by INFERENCE, from
 * a completed round with nothing after it, whenever the event's own status is
 * missing or stale (decision 432 added `events.status` to stop exactly this
 * reading and the fallback is where it survives).
 *
 * The case Kevin caught on the DE cluster's screenshots: a complete DE round,
 * every tracked fencer holding an outcome, and the event line announcing a
 * round that was not coming. Three ways to get there, all real -- rows that
 * predate migration 0024, the round-end repairs (which mark a round complete
 * from the archive while `events.status` is the live poller's alone), and the
 * two writes being two transactions that can come apart.
 *
 * SO THE LINE SAYS NOTHING, rather than saying something else. There is no new
 * state here to name: the fencer rows already carry the outcomes, and the group
 * empties as the grace window retires them, so an event whose people are all
 * done decays out of Active on its own -- gracefully even with a lot of tracked
 * fencers, which is the whole reason no label is needed to cover the gap
 * (Kevin's ruling, decision 463).
 *
 * Scoped to that one label deliberately. "Fencing direct eliminations" with
 * every tracked fencer done is TRUE and stays -- decision 381's settled state
 * is an event running on for people nobody here is watching.
 */
export function withholdRoundState(round: RoundState, fencers: ActiveFencer[]): boolean {
  if (round.key !== "active.round.betweenRounds") return false;
  // No rows is not evidence of anything: a group with no visible fencers is
  // either about to disappear or has nothing to say.
  if (fencers.length === 0) return false;
  return fencers.every((f) => isDone(f));
}

/**
 * The most recent observation anywhere in this event.
 *
 * The event's own `round_status_as_of` is usually the freshest thing, but not
 * always: a strip observed a minute ago while the round status was last read half
 * an hour back means the event is plainly still being covered. Taking the max is
 * what stops the quiet treatment from appearing over rows that are visibly
 * arriving.
 */
/**
 * When we last LOOKED at this event: the poller's last read of its status
 * (`event_checked_at`, migration 0055), or the freshest observation if that is
 * newer -- a DE strip read can land after the schedule read in the same cycle.
 * What the event line's "as of" states.
 *
 * NOT what the went-quiet state reads. That one asks whether the SOURCE has
 * gone silent (decisions 446, 462) and keeps lastObservedIn's change stamps;
 * this one asks how old our own reading is. Before the look was stored, the
 * label used the change stamps too, and between an event's pools and its DE it
 * read "as of 10 min ago" while the poller was reading every few minutes.
 */
export function lastCheckedIn(ev: ActiveEvent): string | null {
  const seen = lastObservedIn(ev);
  const checked = ev.event_checked_at ?? null;
  if (!checked) return seen;
  if (!seen) return checked;
  return new Date(checked) > new Date(seen) ? checked : seen;
}

export function lastObservedIn(ev: ActiveEvent): string | null {
  const stamps = [
    ev.round_status_as_of,
    ev.event_status_as_of,
    ...list(ev.fencers).map((f) => f.as_of),
  ].filter((s): s is string => typeof s === "string");
  if (stamps.length === 0) return null;
  return stamps.reduce((a, b) => (new Date(a) > new Date(b) ? a : b));
}

/**
 * How stale the EVENT's own reporting is, from its freshest observation.
 *
 * §9 LEAVES THE TRIGGER OPEN, and the choice made here is worth knowing about:
 * "Whether a fully-stale event demotes to the quiet treatment automatically:
 * unresolved, revisit with real data." The STATE is required — the screen
 * inventory lists "went-quiet (container opacity)" as something that must be
 * built — so the trigger is the existing third staleness step (30 minutes), the
 * same threshold the datum-level treatment already uses. That keeps one number
 * rather than introducing a second, and it is the conservative reading: an event
 * nobody has heard from in half an hour is being described honestly, not hidden.
 * The real weekend is what settles whether 30 minutes is the right number.
 *
 * An uncovered event has NO tier. There is nothing to have gone quiet, because
 * nothing was ever coming — conflating the two would tell a coach that updates
 * stopped for an event that never had any.
 */
export function coverageTier(ev: ActiveEvent, now: Date = new Date()): StalenessTier {
  if (coverageOf(ev) === "uncovered") return "fresh";
  // A finished event has not gone quiet; it has ended. Nothing more is coming,
  // for the same reason as the uncovered case, and "updates stopped" over an
  // event FTL says is over would be describing the end as a fault.
  if (ev.event_status === "complete") return "fresh";
  // NOR BEFORE IT STARTS: nothing is owed until then. Pools posted the night
  // before put an event at "Fencing pools" by 22:00, the poller ends its run at
  // 23:00 by design (decision 599), and from 23:30 this read "updates stopped"
  // all night (the 2026 October NAC). The day's run opens 90 minutes before the
  // first start and re-stamps the round's status on every read, so once the
  // event is under way the 30 minutes mean what they say. No known start keeps
  // the rule as it was. A two-day event between its days carries its NEXT
  // day's start here (decision 784), so the same rule covers the night and
  // the rest day in between.
  if (beforeStart(ev, now)) return "fresh";
  return stalenessTier(lastObservedIn(ev), "day-of", now);
}

/**
 * The venue-zone calendar date of an instant, as "YYYY-MM-DD".
 *
 * en-CA because its date format IS ISO, so the result compares lexicographically
 * with the wire's date-only strings. A null zone falls back to the device zone,
 * which is all we have — the same reading venueTime takes.
 */
function venueDateOf(now: Date, timeZone: string | null | undefined): string {
  // The server's offset where it has one (decision 785): Firefox's own zone
  // data had Casablanca's day turning an hour early in October 2026.
  const clock = timeZone ? venueClock(timeZone, now) : null;
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: clock ? "UTC" : (timeZone ?? undefined),
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(clock ? shifted(now, clock.offset) : now);
}

/**
 * Whether the day this event RAN is over where it ran (decision 351).
 *
 * This is the backstop Kevin's let-go rule names for orphan cards — fencers
 * with no outcome, because no source ever stated one: a scratch that fell
 * between payload reads, or a registrant FTL never listed. During the event
 * they render as registered, which is honest; once the venue's calendar day is
 * past the event's day there is nothing left to observe, and a card that can
 * never retire would otherwise hold the tournament on Active forever (the
 * 31 Aug report: "the day after the tournament is over, Active is still
 * showing").
 *
 * Keyed on the EVENT's date, falling back to the tournament's end date, so a
 * Saturday event's orphans clear Sunday morning even while the tournament's
 * Sunday events are live. No date at all means never over — the conservative
 * reading, matching isVisible's treatment of a missing timestamp.
 *
 * Typed structurally on the three fields it reads, because Upcoming's
 * done-cascade (decision 357) uses the same venue-day backstop over its own
 * payload shapes — one rule, one implementation, two views.
 */
export function eventDayOver(
  ev: { event_date?: string | undefined },
  t: { end_date?: string | undefined; timezone?: string | null | undefined },
  now: Date = new Date(),
): boolean {
  const day = ev.event_date ?? t.end_date;
  if (!day) return false;
  return venueDateOf(now, t.timezone) > day;
}

/**
 * The fencers still on screen for this event.
 *
 * Group visibility is CLIENT math (contract §0 says so explicitly), and this is
 * where it happens: a done fencer past their grace window drops out here, an
 * orphan card drops out when its event's day is over (decision 351), and an
 * event whose list empties disappears one level up.
 */
export function visibleFencers(
  ev: ActiveEvent,
  t: ActiveTournament,
  policy: GracePolicy,
  now: Date = new Date(),
): ActiveFencer[] {
  // Either word for "over" will do: the event's own status (decision 432) is
  // the authoritative one, and the latest round's completion is what this
  // read before the event had a status of its own.
  const eventComplete = ev.event_status === "complete" || ev.round_status === "complete";
  const dayOver = eventDayOver(ev, t, now);
  return list(ev.fencers).filter((f) => {
    if (!isDone(f)) {
      // A TEAM's card with no outcome, in an event SEEN FINISHED, leaves on the
      // grace window measured from the finish (decision 715): Kevin's let-go
      // rule allows "when all events are finished", and a team the match never
      // found would otherwise sit there for the rest of the day — the bug the
      // October NAC's team cards showed. The finish is passed as the instant to
      // age from, through the same isVisible every card retires by.
      if (f.team_id && ev.event_status === "complete" && ev.event_status_as_of) {
        return isVisible({ tie: false, eliminated_at: ev.event_status_as_of }, policy, now, eventComplete);
      }
      // An orphan — no outcome, nothing to compute a grace window from — stays
      // for the whole day it might yet fence, and leaves with the day.
      return !dayOver;
    }
    return isVisible(f, policy, now, eventComplete);
  });
}

/**
 * The order of a tournament's events: BY START TIME AND NOTHING ELSE (Kevin,
 * after the 2026 October NAC; amends decision 314's urgency order).
 *
 * Earliest `event_starts_at` first, events with no start last, then the event's
 * name, then its id, so the order is total and identical on every poll. The
 * start as STORED: an FTL revision moves an event, and that is accepted, because
 * it is the one change that is about the event itself.
 *
 * WHY NOT URGENCY ANY MORE. 314 ranked events by state — a fresh strip, then
 * pools fencing, then everything else, then gone quiet — and a list that
 * reorders itself when one datum ages makes the reader hunt for the event every
 * time they look. Worse, the rank read two timestamps that legitimately freeze
 * while an event is live: decision 658 holds a round's status once its pools are
 * complete, and pool rows carry no timestamp of their own. So at the NAC the
 * Div I Elite Women's Épée, between its pools and its DE, sank below every
 * event still fencing pools for fifteen minutes. Staleness is still STATED on
 * the card (decisions 454, 462); it no longer MOVES the card.
 */
function compareEventsByStart(a: ActiveEvent, b: ActiveEvent): number {
  const at = (e: ActiveEvent): number | null => {
    if (!e.event_starts_at) return null;
    const v = Date.parse(e.event_starts_at);
    return Number.isNaN(v) ? null : v;
  };
  const byStart = compareInstants(at(a), at(b));
  if (byStart !== 0) return byStart;
  if (a.event_name !== b.event_name) return a.event_name < b.event_name ? -1 : 1;
  if (a.event_id === b.event_id) return 0;
  return a.event_id < b.event_id ? -1 : 1;
}

/**
 * The events still on screen for this tournament, in start-time order (see
 * compareEventsByStart).
 *
 * §5: "When every tracked fencer in an event is done, the whole event group
 * disappears, even if the event is still running." An event group with no fencer
 * cards has nothing to say — it is chrome around an absence — so it goes.
 *
 * Sorted HERE rather than trusted from the server, although `ActiveEvents`
 * orders the same way within a day: the server's order is date-major, and the
 * day axis belongs to activeDays (decision 467), which regroups whatever it is
 * given. Stating the order where it is read keeps it one rule in one place.
 */
export function visibleEvents(
  t: ActiveTournament,
  policy: GracePolicy,
  now: Date = new Date(),
): { event: ActiveEvent; fencers: ActiveFencer[] }[] {
  return list(t.events)
    .map((event) => ({ event, fencers: visibleFencers(event, t, policy, now) }))
    .filter((g) => g.fencers.length > 0)
    .sort((a, b) => compareEventsByStart(a.event, b.event));
}

export interface ActiveGroup {
  tournament: ActiveTournament;
  events: { event: ActiveEvent; fencers: ActiveFencer[] }[];
}

/**
 * The whole screen's visible structure, tournaments with nothing left removed.
 *
 * The caller counts the RESULT to decide whether tournament bands render
 * (principle 9: plural chrome for plural data, band only when 2+). Counting the
 * payload instead would draw a band around a single tournament whenever a second
 * one had just emptied.
 */
export function visibleGroups(
  payload: { tournaments: ActiveTournament[] | null },
  policy: GracePolicy,
  now: Date = new Date(),
): ActiveGroup[] {
  return (
    list(payload.tournaments)
      .map((tournament) => ({ tournament, events: visibleEvents(tournament, policy, now) }))
      .filter((g) => g.events.length > 0)
      // THE MOST IMMEDIATE TOURNAMENT FIRST (Kevin, post-weekend item 5). STABLE,
      // like every sort on this screen: equal instants — and the no-start-time
      // tournaments at the end — keep the server's order, so groups do not
      // shuffle between polls. Only the TOURNAMENT axis moves; the days and the
      // events inside each are untouched (decisions 314, 467).
      .sort((a, b) => compareInstants(tournamentSortInstant(a), tournamentSortInstant(b)))
  );
}

/**
 * When a tournament next needs looking at: the earliest `event_starts_at` among
 * its events still ON SCREEN, or null when none of them has a start time.
 *
 * WHY THE VISIBLE EVENTS AND NOT ALL OF THEM. What is visible already IS the
 * tracked fencers' own events that still hold a card, after the grace window
 * and the venue's day (546) — so the minimum over them is exactly "the earliest
 * upcoming or in-progress event for a tracked fencer". An event that finished
 * and graced out no longer pulls its tournament to the top. That filtering
 * lives only here, in the client, which is why the order is decided here
 * rather than in `ActiveEvents`' SQL.
 *
 * AN IN-PROGRESS EVENT SORTS FIRST without a special case: its start is in the
 * past, so it is earlier than anything still to come.
 *
 * The EVENT's start rather than any fencer's `starts_at`, because the live-FTL
 * gate withholds the fencer-level time from accounts without it.
 */
export function tournamentSortInstant(group: ActiveGroup): number | null {
  let earliest: number | null = null;
  for (const { event } of group.events) {
    if (!event.event_starts_at) continue;
    const at = Date.parse(event.event_starts_at);
    if (Number.isNaN(at)) continue;
    if (earliest === null || at < earliest) earliest = at;
  }
  return earliest;
}

/** Ascending, nulls last, and 0 for a tie so a stable sort keeps the input order. */
function compareInstants(a: number | null, b: number | null): number {
  if (a === b) return 0;
  if (a === null) return 1;
  if (b === null) return -1;
  return a - b;
}

/**
 * One day's worth of a tournament's visible events.
 *
 * `date` is the event's own `event_date`, a calendar day as YYYY-MM-DD, or null
 * for events no source has dated. NOT a label: the screen formats it, because
 * direction §7 keeps every string out of here.
 */
export interface ActiveDay {
  date: string | null;
  events: ActiveGroup["events"];
}

/**
 * Split a tournament's visible events into days, preserving their order.
 *
 * WHY THIS EXISTS. Active states TIMES and never days, which is right inside one
 * day and ambiguous across three. A tournament band reading "Fri 4 – Sun 6 Sep"
 * above a list whose first event is Saturday morning is easy to read as "this
 * one is Friday" — reported from the deployed app on 2026-09-03, against a real
 * tournament running Fri–Sun with no tracked events on the Friday.
 *
 * The events were already in day order (`view_active.sql` orders by
 * `e.event_date, e.starts_at NULLS LAST, e.name`), so nothing about the sort was
 * wrong. What was missing was any mark that made the order LEGIBLE.
 *
 * DAYS RUN IN CALENDAR ORDER; START TIME ORDERS THE EVENTS INSIDE A DAY. The
 * two sorts answer different questions and the day axis is the outer one. (The
 * history below is about the urgency order this replaced; the regrouping it
 * argues for is what keeps the day axis independent of whatever sorts the
 * events, and it stays.)
 *
 * This function used to preserve the incoming order outright, on the reasoning
 * that the server had already ordered by `event_date, starts_at NULLS LAST,
 * name`. That was true of the SERVER's list and false of the one this function
 * actually receives: `visibleEvents` re-sorts the whole tournament by urgency
 * first (decision 314), so by the time the events arrive here their dates are
 * in whatever order the urgency ranks happened to fall in. Walking that list
 * and starting a new group at every change of date therefore inherited the
 * urgency order onto the DAY axis.
 *
 * Reported by Kevin against the demo corpus, and the mechanism is exact: once
 * a Thursday event passes the 30-minute quiet step it ranks 3, while a Friday
 * event that has not started ranks 2 — so Friday sorted above Thursday and the
 * card read "Fri 11 Sep" before "Thu 10 Sep". A date heading is a promise
 * about sequence, and that one was lying.
 *
 * Decision 314 predates the day markers (deviation #27), so this interaction
 * was never ruled on. The reading taken here: urgency ordering exists to
 * answer "which event do I look at now", which is a question WITHIN a day; the
 * day axis is a calendar fact and cannot be reordered by how interesting its
 * contents are. Within each day the entries keep the urgency order they
 * arrived in, so 314 is untouched where it applies. A single-day tournament —
 * the overwhelming majority — is completely unaffected, because there is only
 * one group to order.
 *
 * GROUPED BY DATE RATHER THAN BY RUNS, which also closes a second-order bug the
 * old comment described as a feature: with an urgency-ordered input, the same
 * day could appear twice (Thu, Fri, Thu) and would render its heading twice.
 * A Map keyed on the date makes that unrepresentable.
 *
 * AN UNDATED EVENT GETS ITS OWN GROUP WITH A NULL DATE, rather than joining the
 * day above it. Contract §0: null renders nothing, and filing an event under a
 * day no source assigned it to would be inventing the one fact this whole change
 * exists to state. It sorts LAST, matching the server's own `starts_at NULLS
 * LAST` posture — an event nobody has dated cannot be placed among ones that
 * have been. Zero of the 46 events on FTL-covered tournaments in the corpus are
 * undated, so this is a guard rather than a live case.
 */
export function activeDays(events: ActiveGroup["events"]): ActiveDay[] {
  const byDate = new Map<string | null, ActiveDay>();
  for (const entry of events) {
    const date = entry.event.event_date ?? null;
    const day = byDate.get(date);
    if (day) day.events.push(entry);
    else byDate.set(date, { date, events: [entry] });
  }

  // Lexicographic on the date, which is correct and not a shortcut: these are
  // SQL DATEs on the wire as "2026-09-05" — zero-padded, fixed width, no zone —
  // so string order is calendar order. Parsing them to compare would reopen the
  // UTC-shift trap format.ts documents at length.
  return [...byDate.values()].sort((a, b) => {
    if (a.date === b.date) return 0;
    if (a.date === null) return 1;
    if (b.date === null) return -1;
    return a.date < b.date ? -1 : 1;
  });
}

/**
 * Whether this tournament's events need their days stated.
 *
 * THE TOURNAMENT'S SPAN DECIDES, NOT THE VISIBLE EVENTS', and that is the whole
 * subtlety. The obvious rule — "more than one day of events is showing" — misses
 * the worst case: a Fri–Sun tournament with tracked events on Saturday only
 * reads exactly as badly, because the band still says Friday and there is still
 * nothing saying which day the list is. A one-day tournament needs no separator,
 * because there is no other day to confuse it with.
 */
export function needsDayMarkers(t: {
  start_date?: string | undefined;
  end_date?: string | undefined;
}): boolean {
  return !!t.start_date && !!t.end_date && t.start_date !== t.end_date;
}

/**
 * Whether this tournament crosses a month boundary, which is what decides
 * whether its day markers name the month (build rule 7).
 *
 * THE TOURNAMENT'S SPAN, not the marker's own date, for the same reason
 * needsDayMarkers uses it: the question is what disambiguates WITHIN the group
 * being read, and the group is the tournament. A Thu 10 – Fri 11 Sep tournament
 * has said "Sep" once on its band and its markers need only "Thu 10"; a Nov 30 –
 * Dec 1 one keeps the month on both, because there it is the separator.
 *
 * Compared as STRINGS on the "YYYY-MM" prefix, which is exact rather than
 * approximate: these are SQL DATEs on the wire, zero-padded and fixed width, so
 * the prefix is the calendar month. Parsing them to compare would reopen the
 * UTC-shift trap format.ts documents at length — the same argument activeDays
 * makes for sorting them lexicographically.
 */
export function tournamentSpansMonths(t: {
  start_date?: string | undefined;
  end_date?: string | undefined;
}): boolean {
  if (!t.start_date || !t.end_date) return false;
  return t.start_date.slice(0, 7) !== t.end_date.slice(0, 7);
}

/**
 * Which empty state to show, when there is nothing to render.
 *
 * The two are genuinely different days. "everyone-done" is the day ENDING: the
 * payload still has live events, and every tracked fencer in them has finished —
 * §9 wants that stated plainly ("Everyone's done — results are in Past") and
 * never as an error. "nothing-live" is a Tuesday: no tracked tournament is
 * running at all, and pointing at Past would be a non-sequitur.
 *
 * Distinguishable only because the server does NOT filter done fencers out, which
 * is the payoff for keeping the grace window on the client.
 */
export type EmptyReason = "everyone-done" | "nothing-live";

export function emptyReason(payload: { tournaments: ActiveTournament[] | null }): EmptyReason {
  const anyFencerAtAll = list(payload.tournaments).some((t) =>
    list(t.events).some((e) => list(e.fencers).length > 0),
  );
  return anyFencerAtAll ? "everyone-done" : "nothing-live";
}

/**
 * §9's field ladder for a fencer's personal-state line.
 *
 * "DE rows render exactly the fields that exist — strip and/or published time
 * ('On strip at 2:40 PM', 'On strip', '2:40 PM') or, when neither is published
 * (most tournaments, until late rounds), just name + bracket level + the event
 * link."
 *
 * The phrase carries TENSE, which is why "On strip" is not redundant beside the
 * strip number rendered at 26px in the trailing slot: a strip with no time means
 * go now, a time with no strip means later, and both together mean a specific
 * strip at a specific time. The number alone cannot say which.
 */
export type WhenKey = "onStripAt" | "onStrip" | "atTime" | null;

/**
 * `withoutTime` is set for a pool row in an UNFLIGHTED event, where the event
 * header states the one shared start time and repeating it per fencer is the same
 * fact N times over.
 *
 * It removes the TIME from the ladder rather than the whole line, which is the
 * part worth being careful about: "On strip at 9:00 AM" becomes "On strip", not
 * nothing. The tense is what the phrase is for — a strip with no time means go
 * now — and that statement is still true and still not made by the number alone.
 */
export function whenKeyFor(f: ActiveFencer, opts: { withoutTime?: boolean } = {}): WhenKey {
  const hasStrip = f.strip != null && f.strip !== "";
  const hasTime = f.starts_at != null && !opts.withoutTime;
  if (hasStrip && hasTime) return "onStripAt";
  if (hasStrip) return "onStrip";
  if (hasTime) return "atTime";
  return null;
}

// The DE table's name from its size lives in deTable.ts, where the service
// worker can reach it too (decision 951); re-exported for this file's callers.
export { deTableLabel } from "./deTable";

/**
 * The pool indicator as a fencer reads it off a pool sheet: ALWAYS signed, so
 * "+11" and "-3", and zero is "+0" rather than a bare "0" that reads as a
 * missing number (post-Remenyik item 4, Kevin's format). ASCII hyphen-minus,
 * matching the "4-1" beside it and what the venue board prints.
 */
export function signedIndicator(n: number): string {
  return n < 0 ? `-${Math.abs(n)}` : `+${n}`;
}

/**
 * The pool record line's values: wins, losses and the signed indicator. Losses
 * are bouts fenced minus bouts won -- the wire sends FTL's two counts, never a
 * derived third (416). Null when there is no record to state.
 */
export function poolRecordValues(
  record: ActiveFencer["pool_record"],
): { wins: number; losses: number; indicator: string } | null {
  if (!record) return null;
  return {
    wins: record.victories,
    losses: Math.max(0, record.bouts - record.victories),
    indicator: signedIndicator(record.indicator),
  };
}

/**
 * §9's opponent line, un-deferred (deviations #30): where a fencer is in the DE
 * and what the DRAW says about her next bout.
 *
 * TWO SOURCES, ONE LINE. The bracket's own reading is an OBSERVATION and wins
 * outright (decision 458); the arithmetic over the pool positions is an
 * EXPECTATION and fills the gap before the bracket exists, which is the ten
 * minutes when the question is asked most (decision 457). The server already
 * withholds the projection once the bracket has spoken, so the precedence here
 * is belt and braces rather than the only guard -- and it is stated anyway,
 * because a client that rendered both would be stating one fact twice in two
 * voices.
 *
 * Three opponent shapes, and the distinction between the last two is the honest
 * part:
 *
 *   vs        one person: the bracket names them, or the draw does
 *   winnerOf  a bye, so the opponent is whoever wins a bout not yet fenced --
 *             two names, and neither is guessed at
 *   bye       a bye with nothing yet to say about what follows it
 *
 * The TABLE is independent of all three, because "Table of 16" is worth saying
 * on its own: it is the whole status line for a fencer the strips queue has
 * drained past, which decision 127 makes the ordinary case at most venues.
 */
export type OpponentLabel = {
  /**
   * `eitherOf` is two possible opponents -- fencers tied in the pools, either
   * of whom holds the opponent's seed (decision 690): "vs A or B", not the
   * winner of a bout. `orBye` is the one other two-way case (after the 2026
   * October NAC): her own tied place straddles the bye line, so it is "vs A or
   * a bye". The wire says it as one name with both `bye` and `tied` set.
   */
  kind: "vs" | "winnerOf" | "eitherOf" | "orBye" | "bye";
  /** The named opponent, or the first of the two candidates. Empty for `bye`. */
  a: string;
  /** The second candidate, present only for `winnerOf` and `eitherOf`. */
  b: string;
  bye: boolean;
  /**
   * Who wins a tied bout under a P-black card (post-Remenyik item 6): the
   * higher initial seed, which is the LOWER number. `pA`/`pB` mark the
   * candidates; `pSelf` marks the tracked fencer. See priorityMarks.
   */
  pA: boolean;
  pB: boolean;
  pSelf: boolean;
};

/**
 * The P marker's rule (post-Remenyik item 6). "Higher seed" is the LOWER
 * number, and every comparison needs both seeds: an unknown one means no P for
 * that comparison.
 *
 * - Each opponent who outranks the tracked fencer gets a P.
 * - The tracked fencer gets one only if they outrank EVERY named opponent --
 *   for "the winner of A / B", whichever of the two arrives.
 *
 * Facts in, booleans out: the server sends seeds (416) and the comparison is
 * the client's, as the matchup it decorates already is.
 */
export function priorityMarks(
  self: number | null | undefined,
  opponents: (number | null | undefined)[],
): { self: boolean; opponents: boolean[] } {
  const known = (n: number | null | undefined): n is number =>
    typeof n === "number" && Number.isFinite(n) && n > 0;
  const marks = opponents.map((o) => known(self) && known(o) && o < self);
  const selfWins =
    known(self) && opponents.length > 0 && opponents.every((o) => known(o) && self < o);
  return { self: selfWins, opponents: marks };
}

export type DeStatus = {
  /** Our own words for the table, when we recognise the heading. */
  table: { key: string; count: number } | null;
  /**
   * What FTL printed, when we do not. §9's rule: the raw string is quoted in a
   * disclosure and never used as the label — so this is only ever set where
   * `table` is null, and the pair is the same shape the round state uses.
   */
  tableRaw: string | null;
  opponent: OpponentLabel | null;
};

export function deStatusOf(f: ActiveFencer): DeStatus | null {
  const observedTable = deTableLabel(f.table_size ?? 0);
  const raw = observedTable ? null : (f.table_raw ?? null);

  // The bracket named the opponent: an observation, and the end of it.
  if (f.opponent) {
    const p = priorityMarks(f.initial_seed, [f.opponent_initial_seed]);
    return {
      table: observedTable,
      tableRaw: raw,
      opponent: {
        kind: "vs",
        a: f.opponent,
        b: "",
        bye: false,
        pA: p.opponents[0] ?? false,
        pB: false,
        pSelf: p.self,
      },
    };
  }

  const p = f.projected_opponent;
  if (!p) {
    // A table on its own is still an answer — see the type's note.
    if (observedTable || raw) return { table: observedTable, tableRaw: raw, opponent: null };
    return null;
  }

  // Names and their seeds stay paired through the filter: initial_seeds runs
  // parallel to names on the wire.
  const named = (p.names ?? [])
    .map((name, i) => ({ name, seed: p.initial_seeds?.[i] ?? null }))
    .filter((x) => x.name !== "");
  const [a, b] = named.map((x) => x.name);
  const marks = priorityMarks(
    f.initial_seed,
    named.map((x) => x.seed),
  );
  const pA = marks.opponents[0] ?? false;
  const pB = marks.opponents[1] ?? false;
  // The projection carries its own table, which is the bout's rather than the
  // one she byed through; where the bracket has also been read they agree, and
  // the observed one is preferred for being the observation.
  const table = observedTable ?? deTableLabel(p.table_size);
  const tableRaw = table ? null : raw;
  // Named fields rather than the array the wire uses, because the two shapes
  // interpolate into two different sentences and a component indexing into a
  // list would have to re-check the length to know which.
  if (a == null) {
    if (!p.bye) return { table, tableRaw, opponent: null };
    // A bye has nobody to compare against, so no P.
    return {
      table,
      tableRaw,
      opponent: { kind: "bye", a: "", b: "", bye: true, pA: false, pB: false, pSelf: false },
    };
  }
  if (b == null) {
    // One name with BOTH flags is the tie across the bye line: that fencer or
    // a bye. `bye` alone with one name is a bye whose next bout is known.
    if (p.bye && p.tied) {
      return {
        table,
        tableRaw,
        opponent: { kind: "orBye", a, b: "", bye: false, pA, pB: false, pSelf: marks.self },
      };
    }
    return {
      table,
      tableRaw,
      opponent: { kind: "vs", a, b: "", bye: p.bye, pA, pB: false, pSelf: marks.self },
    };
  }
  return {
    table,
    tableRaw,
    opponent: { kind: p.tied ? "eitherOf" : "winnerOf", a, b, bye: p.bye, pA, pB, pSelf: marks.self },
  };
}
